export const EMAIL_REPLIES = {
  cei1: {
    subject: 'Re: Q1 Revenue Projections for Board Prep',
    recipient: 'Vikram Rao',
    thinkingTime: 12,
    thinkingSteps: [
      { label: 'Thinking...', detail: 'Analyzing Vikram\'s request and board prep context' },
      { label: 'Fetching past conversations...', detail: 'Pulling 3 previous threads with Vikram, last board deck data from Neel' },
      { label: 'Drafting response...', detail: 'Combining Sally\'s Q1 forecast, deal slippage context, and v2.3 support update' },
    ],
    intro: "Vikram needs Q1 numbers for the Friday board prep.\n\nFairly straightforward — but he's also asking about the v2.3 situation, so I want to make sure we frame that carefully.\n\nI pulled the latest from Sally, Finn, and Zira to put together a complete picture.",
    draft: `Hi Vikram,

Thanks for the heads-up on the Friday prep call. Here's the latest:

**Q1 Revised Forecast: $342K** (down from $380K)
- Two enterprise deals slipped to Q2: TechCorp ($22K, budget freeze) and DataFlow ($16K, budget freeze — not product-related)
- SMB pipeline is +22% above target, offsetting ~40% of the gap
- Win rate improved to 34% (from 28%) driven by the new pricing tier

**v2.3 Support Situation:**
- Ticket volume spiked 40% post-release, but root cause identified (3 specific bugs)
- Hotfix deploying within 48 hours, SLA recovery expected in 3-5 days
- 5 churn-risk accounts being proactively managed (all received personalized outreach)

**Board Narrative:** Revenue dip is timing-driven, not structural. SMB momentum is real and sustainable. The v2.3 issue is contained with a clear resolution path.

I'll have Neel's cashflow model updated with the final numbers by Wednesday EOD.

Best,
Arjun`,
    draftPrompt: "Take a look — I'd keep Vikram's tone factual since he's prepping for the board. Want to tweak anything, or shall I send it as-is? You can also schedule it for later if you'd prefer.",
    attachments: [
      { name: 'Q1_Revenue_Forecast_v3.xlsx', type: 'spreadsheet', size: '245 KB' },
      { name: 'Deal_Pipeline_Summary.pdf', type: 'pdf', size: '1.2 MB' },
    ],
    sentConfirm: "Sent! Vikram should have it in his inbox now. I also CC'd Neel so he knows you've referenced the cashflow model — saves you a separate ping. 4 more emails in the stack — want to keep going?",
    draftConfirm: "Saved to drafts. I'll keep the attachments linked so they're ready when you come back to it. Want to tackle the next one?",
    remindConfirm: "Got it, I'll nudge you {time}. I'll also keep the draft warm so you can fire it off quickly. Moving on —",
    editScenario: {
      userMsg: "Tone down the v2.3 section — Vikram gets nervous about bugs in front of the board. Frame it as proactive quality management, not firefighting. Also, drop the churn-risk line entirely.",
      thinkingTime: 4,
      lucResponse: "Good call — Vikram reads between the lines. I've softened the v2.3 section to focus on the resolution path rather than the problem, and removed the churn-risk mention. The narrative now reads as proactive monitoring, not damage control.",
      revisedDraft: `Hi Vikram,

Thanks for the heads-up on the Friday prep call. Here's the latest:

**Q1 Revised Forecast: $342K** (down from $380K)
- Two enterprise deals slipped to Q2: TechCorp ($22K, budget freeze) and DataFlow ($16K, budget freeze — not product-related)
- SMB pipeline is +22% above target, offsetting ~40% of the gap
- Win rate improved to 34% (from 28%) driven by the new pricing tier

**v2.3 Quality Update:**
- Post-release monitoring identified 3 optimization opportunities — all addressed in the upcoming patch
- Deploying within 48 hours with full SLA recovery expected in 3-5 days
- All impacted accounts have received proactive outreach with resolution timelines

**Board Narrative:** Revenue dip is timing-driven, not structural. SMB momentum is real and sustainable. Engineering is executing a disciplined release process with fast iteration cycles.

I'll have Neel's cashflow model updated with the final numbers by Wednesday EOD.

Best,
Arjun`,
    },
  },
  cei2: {
    subject: 'Re: Q2 Check-in & Continued Interest',
    recipient: 'Lisa Chen',
    thinkingTime: 6,
    thinkingSteps: [
      { label: 'Thinking...', detail: 'Reviewing Lisa\'s message and DataFlow relationship history' },
      { label: 'Fetching past conversations...', detail: 'Pulling CRM notes, Sally\'s deal history, last 2 email threads with Lisa' },
      { label: 'Drafting response...', detail: 'Crafting warm follow-up that keeps the door open for Q2' },
    ],
    intro: "Good news here — Lisa's explicitly saying the freeze isn't about us. She even mentioned Implify to their new VP of Engineering.\n\nThat's a strong buy signal. I want to keep the momentum without being pushy.",
    draft: `Hi Lisa,

Good to hear the freeze isn't related to us — and really appreciate you keeping the conversation alive. The VP of Engineering mention is a great signal.

Happy to keep the proposal active. I'll have Sally set up a check-in for late February — does the week of Feb 24 work for your team? We'll come prepared with:
- Updated product roadmap (some exciting things in Q2 you'll want to see)
- Revised implementation timeline based on your team's availability
- A custom demo of the workflow automation features your VP mentioned interest in

In the meantime, if anything changes on the budget front, don't hesitate to reach out. We're flexible on timing and structure.

Looking forward to it,
Arjun`,
    draftPrompt: "I kept it warm but not desperate — the VP mention is our hook for Q2. Want to edit anything, or good to send?",
    attachments: [],
    sentConfirm: "On its way to Lisa! I've also flagged Sally to prep the Feb check-in. That VP angle could turn this from a $16K deal into something bigger. What's next?",
    draftConfirm: "Draft saved. The DataFlow thread is looking promising — good to come back to this one fresh. Next email?",
    remindConfirm: "Reminder set for {time}. I'll prep a calendar invite for the Feb check-in in the meantime. Let's move on —",
    editScenario: {
      userMsg: "Add a line about our pricing flexibility — mention we can do quarterly billing instead of annual if that helps with their budget cycle. Also hint at the founder discount for early adopters.",
      thinkingTime: 3,
      lucResponse: "Smart move — giving Lisa ammunition for the budget conversation. I've added the quarterly billing option and a subtle founder discount mention without making it feel discounted. The tone stays premium.",
      revisedDraft: `Hi Lisa,

Good to hear the freeze isn't related to us — and really appreciate you keeping the conversation alive. The VP of Engineering mention is a great signal.

Happy to keep the proposal active. I'll have Sally set up a check-in for late February — does the week of Feb 24 work for your team? We'll come prepared with:
- Updated product roadmap (some exciting things in Q2 you'll want to see)
- Revised implementation timeline based on your team's availability
- A custom demo of the workflow automation features your VP mentioned interest in

One thing worth mentioning — we've introduced quarterly billing for teams in your situation. It might help align with your budget cycle without requiring a full annual commitment upfront. And as one of our earliest conversations, I'd want to make sure the pricing reflects that relationship.

In the meantime, if anything changes on the budget front, don't hesitate to reach out. We're flexible on timing and structure.

Looking forward to it,
Arjun`,
    },
  },
  cei3: {
    subject: 'Re: Sprint 14 PTO — Meera & Raj Overlap',
    recipient: 'Meera Rao',
    thinkingTime: 4,
    thinkingSteps: [
      { label: 'Thinking...', detail: 'Understanding the PTO conflict between Meera and Raj' },
      { label: 'Fetching past conversations...', detail: 'Checking Tali\'s team calendar, Raj\'s PTO request, sprint capacity data' },
      { label: 'Drafting response...', detail: 'Proposing a fair split that keeps sprint coverage intact' },
    ],
    intro: "Classic PTO conflict — Meera and Raj both want Sprint 14 off. Tali flagged this earlier.\n\nI've worked out a split that respects Meera's family commitments while keeping at least one senior frontend dev available throughout the sprint.",
    draft: `Hi Meera,

Thanks for flagging the overlap early. I've looked into this with Tali and here's what we can do:

**Approved:** Your PTO for Feb 14-17 (the family commitment days) is confirmed.

**Proposed adjustment:** Could you shift the rest to Feb 18-21 instead of Feb 10-13? That way we maintain coverage during the first half of Sprint 14, and Raj can take Feb 10-14 as he requested.

This gives us at least one senior frontend dev available throughout the sprint. If the Feb 18-21 dates don't work for you, let me know and we'll find another arrangement.

I'll confirm with Raj today so you can both finalize travel by Wednesday.

Best,
Arjun`,
    draftPrompt: "I think this is fair — Meera gets her priority dates locked in, and the team stays covered. Want to adjust anything before I send it?",
    attachments: [],
    sentConfirm: "Sent to Meera! I'll ping Tali to confirm Raj's dates too so this is fully resolved by EOD. People stuff done — ready for the next one?",
    draftConfirm: "Draft saved. I'd suggest sending this today so Meera can finalize her travel plans. Want to continue through the inbox?",
    remindConfirm: "Will remind you {time}. Heads up though — Meera asked for resolution by Wednesday, so don't sit on this too long. Next?",
  },
  cei4: {
    subject: 'Re: API Integration Issues — Resolution Timeline',
    recipient: 'James Park',
    thinkingTime: 15,
    thinkingSteps: [
      { label: 'Thinking...', detail: 'This one needs careful handling — James is frustrated and renewal is close' },
      { label: 'Fetching past conversations...', detail: 'Pulling v2.3 incident data from Stark, TechCorp account history from Sally, Zira\'s churn assessment' },
      { label: 'Drafting response...', detail: 'Balancing accountability with confidence — CEO-to-CTO tone' },
    ],
    intro: "This is the sensitive one. James is frustrated — rightfully so — and his renewal is in 45 days. I've drafted this as a CEO-to-CTO response: direct accountability, concrete timeline, and a clear path forward. No corporate fluff. I also pulled the incident report from Stark so we can attach it — transparency builds trust here.",
    draft: `James,

I want to personally apologize for the disruption the v2.3 release has caused to your production systems. This is unacceptable, and I take full responsibility.

Here's where we stand:

**Immediate Actions (already underway):**
- Engineering has identified the root cause: 3 specific bugs in the API auth and data sync modules
- Hotfix is our #1 sprint priority — ETA for deployment: Thursday
- Your dedicated support contact (Zira) will provide hourly status updates starting today

**For your assurance:**
- I'm scheduling a call with you this week — my EA will reach out to find a time that works
- We're implementing additional regression testing specifically for API integrations to prevent recurrence
- Your renewal is important to us, and I want to discuss how we can make this right

**Attached:** Current incident report with timeline and resolution plan.

I understand trust is earned back through action, not words. You'll see the fix deployed before the end of the week.

Regards,
Arjun Mehta, CEO`,
    draftPrompt: "I went with direct accountability — no hedging. James respects candor based on past interactions. The incident report attachment is important here. Want to soften anything, or send it as-is?",
    attachments: [
      { name: 'v2.3_Incident_Report.pdf', type: 'pdf', size: '890 KB' },
      { name: 'TechCorp_Resolution_Plan.pdf', type: 'pdf', size: '420 KB' },
    ],
    sentConfirm: "Sent directly to James. I've also alerted Zira to start the hourly updates and told Sally to prep the renewal conversation. This one's high-stakes but we're handling it right. Onwards?",
    draftConfirm: "Saved. Fair warning — James asked for a response from leadership, so I'd prioritize sending this today. The longer we wait, the more frustrated he gets. Next email?",
    remindConfirm: "Reminder set for {time}. But honestly? I'd send this sooner rather than later — James's patience is thin and the competitor is circling. Your call though. What's next?",
  },
  cei5: {
    subject: 'Re: 90-Day Cashflow Model Sign-off',
    recipient: 'Neel Kapoor',
    thinkingTime: 9,
    thinkingSteps: [
      { label: 'Thinking...', detail: 'Reviewing Neel\'s cashflow model and board narrative options' },
      { label: 'Fetching past conversations...', detail: 'Pulling Finn\'s projections, today\'s planning sync decisions, hire approval status' },
      { label: 'Drafting response...', detail: 'Aligning sign-off with board narrative direction decided this morning' },
    ],
    intro: "Neel's cashflow model is solid — all three scenarios maintain 6+ months runway.\n\nThe main decision is narrative framing: I'm recommending we flag the Senior Dev hire as 'planned' since we greenlit it in this morning's sync.\n\nAlso adding a sensitivity analysis request that'll preempt the board's obvious question.",
    draft: `Hi Neel,

Great work on the model — I've reviewed all three scenarios and the numbers look solid. Six months of runway in every scenario is exactly where we need to be.

**Sign-off:** Approved. A few notes on the narrative:

1. **Senior Developer hire:** Flag it as "planned" — we've already greenlit this in today's planning sync. No ambiguity needed.

2. **Revenue narrative:** Frame the Q1 revision as "timing adjustment, not demand signal." The SMB pipeline strength is the real story — make sure that's prominent.

3. **v2.3 context:** Include a brief note that it's a known, contained issue with a 48-hour resolution path. Don't let the board overweight it.

One addition: Can you add a sensitivity analysis on what happens if we recover TechCorp but lose DataFlow? I think that's the most likely scenario and the board will ask about it.

Wednesday EOD deadline works. Let's do a 15-min alignment call tomorrow afternoon.

Best,
Arjun`,
    draftPrompt: "Clean and decisive — Neel will appreciate the clear direction on the narrative. The sensitivity analysis ask will make us look proactive in front of the board. Ready to send?",
    attachments: [
      { name: '90Day_Cashflow_Model_v2.xlsx', type: 'spreadsheet', size: '380 KB' },
    ],
    sentConfirm: "Done! Neel's got his marching orders. That sensitivity analysis is going to save us from an awkward board question — good move. That wraps up the priority inbox!",
    draftConfirm: "Saved as draft. Neel's deadline is Wednesday, so you've got a bit of breathing room on this one. Shall we continue?",
    remindConfirm: "I'll remind you {time}. Neel's working to a Wednesday deadline so no rush, but don't forget. Let's keep moving —",
  },
};

// New emails that arrive during the workday (clock-driven)
export const INCOMING_EMAILS = [
  {
    id: 'cei6',
    sender: 'Sarah Kim (Acme Corp)',
    senderInitials: 'SK',
    time: '11:30 AM',
    arriveAtMinute: 11 * 60 + 30,
    summary: 'Loved the demo — sharing with our VP of Engineering this afternoon.',
    expandedSummary: 'Sarah is forwarding the proposal and demo recording to their VP of Engineering. She wants to schedule a technical deep-dive next week with both teams. Asking for available slots Tuesday-Thursday.',
  },
  {
    id: 'cei7',
    sender: 'Alex Rivera (Product Hunt)',
    senderInitials: 'AR',
    time: '1:15 PM',
    arriveAtMinute: 13 * 60 + 15,
    summary: 'Implify nominated for "Product of the Week" — need your quote by 3 PM.',
    expandedSummary: 'Product Hunt team selected Implify for their weekly spotlight. They need a 2-3 sentence founder quote and a hero image by 3 PM today. The feature goes live Thursday morning. Expected traffic: 5,000-8,000 unique visitors.',
    attachments: [{ name: 'PH_Spotlight_Brief.pdf', type: 'pdf', size: '156 KB' }],
  },
  {
    id: 'cei8',
    sender: 'Board Admin',
    senderInitials: 'BA',
    time: '3:45 PM',
    arriveAtMinute: 15 * 60 + 45,
    summary: 'Friday board meeting moved to Thursday 2 PM — updated agenda attached.',
    expandedSummary: 'The quarterly board meeting has been rescheduled from Friday 10 AM to Thursday 2 PM due to a scheduling conflict with two board members. Updated agenda includes the Q1 forecast review, engineering capacity discussion, and a new item on competitive landscape.',
    attachments: [{ name: 'Board_Agenda_Q1_Updated.pdf', type: 'pdf', size: '210 KB' }],
  },
];

export const INCOMING_EMAIL_REPLIES = {
  cei6: {
    subject: 'Re: Technical Deep-Dive Scheduling',
    recipient: 'Sarah Kim',
    thinkingTime: 5,
    thinkingSteps: [
      { label: 'Thinking...', detail: 'Sarah\'s excited — let\'s capitalize on the momentum' },
      { label: 'Fetching past conversations...', detail: 'Checking your calendar, pulling the demo follow-up checklist' },
      { label: 'Drafting response...', detail: 'Setting up the technical deep-dive with available slots' },
    ],
    intro: "Sarah's forwarding us to their VP of Engineering — that's exactly the buying signal we wanted from the Acme deal.\n\nStrike while the iron's hot. I've checked your calendar and have three clean slots for the deep-dive.",
    draft: `Hi Sarah,

That's great to hear! Happy to set up a technical deep-dive with your VP of Engineering.

**Available slots next week:**
- Tuesday 2-3:30 PM
- Wednesday 10-11:30 AM
- Thursday 3-4:30 PM

We'll bring our CTO and a solutions engineer to cover:
- API architecture and integration patterns
- Workflow automation engine deep-dive
- Security and compliance overview
- Custom demo with your specific use cases

Let me know which slot works best and I'll send the calendar invite with a prep doc.

Best,
Arjun`,
    draftPrompt: "Quick response here is key — Sarah's sharing with the VP today, so let's be top of mind. Anything you'd change?",
    attachments: [],
    sentConfirm: "Fired off to Sarah! I've also pinged the CTO to block all three slots until we hear back. Acme's looking strong at $48K/year. Next?",
    draftConfirm: "Saved. Don't sit on this one though — the VP meeting could happen today. Ready for the next email?",
    remindConfirm: "Reminder set for {time}. Though seriously, the sooner the better on this one — Sarah's pitching us to her VP today. Moving on —",
  },
  cei7: {
    subject: 'Re: Product of the Week — Founder Quote',
    recipient: 'Alex Rivera',
    thinkingTime: 8,
    thinkingSteps: [
      { label: 'Thinking...', detail: 'Product Hunt feature — great for brand visibility' },
      { label: 'Fetching past conversations...', detail: 'Pulling brand guidelines, previous PR quotes, Wright\'s messaging framework' },
      { label: 'Drafting response...', detail: 'Crafting a punchy founder quote aligned with PLG positioning' },
    ],
    intro: "Nice! Product Hunt spotlight could drive 5-8K visitors. I worked with Wright's messaging framework to draft a quote that reinforces our PLG positioning. The 3 PM deadline is tight, so let's move fast on this one.",
    draft: `Alex,

Honored to be selected! Here's the quote:

"We built Implify because SMB founders shouldn't need a team of 10 to operate like a team of 100. Our AI agents don't replace your team — they amplify what your best people are already doing, with full transparency into every decision."

Hero image attached. Let me know if you need any adjustments.

Thanks for the feature!
Arjun`,
    draftPrompt: "Short, punchy, on-brand. The 'amplify not replace' angle resonates well with the PH audience. Want to wordsmith it, or fire it off?",
    attachments: [],
    sentConfirm: "Quote sent! I've told Marsi to prep the landing page for the PH traffic spike Thursday morning — we should capture those visitors. What's next?",
    draftConfirm: "Saved — but remember, Alex needs this by 3 PM today. Set a reminder if you want to come back to it. Next?",
    remindConfirm: "Will remind you {time}. But the deadline is 3 PM today, so keep that in mind. What's next?",
  },
  cei8: {
    subject: 'Re: Board Meeting Reschedule Confirmation',
    recipient: 'Board Admin',
    thinkingTime: 3,
    thinkingSteps: [
      { label: 'Thinking...', detail: 'Board meeting moved up — need to check calendar conflicts' },
      { label: 'Fetching past conversations...', detail: 'Checking Thursday calendar, pulling board prep status from Neel' },
      { label: 'Drafting response...', detail: 'Confirming attendance and flagging prep timeline adjustment' },
    ],
    intro: "Board meeting moved to Thursday — that tightens our prep window.\n\nGood news is Neel's already working on the cashflow model and you've signed off on the narrative direction. I'll make sure Dora preps the competitive landscape item since that's new on the agenda.",
    draft: `Confirmed — Thursday 2 PM works.

I'll have the following ready by Wednesday EOD:
- Revised Q1 forecast with narrative (Neel is finalizing)
- Engineering capacity report and hiring update
- Competitive landscape analysis (new item — Dora will prepare)

Please share the updated Zoom link when available.

Best,
Arjun`,
    draftPrompt: "Simple confirmation — the board admin just needs a yes. Want to add anything, or ship it?",
    attachments: [],
    sentConfirm: "Confirmed with the board admin. I've already told Dora to start on the competitive landscape analysis — that's the new agenda item we need to prep for. All done with emails for now!",
    draftConfirm: "Saved as draft. This is time-sensitive since it's a board reschedule — I'd confirm today. Want to continue?",
    remindConfirm: "Reminder set. Board admin replies are usually quick confirms — I'd just send it. Anyway, let's continue —",
  },
};
